import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  envoyerFichiersDepot,
  formatDate,
  formatOctets,
  type SuiviEnvoiFichier,
} from '@artosera/shared';
import { Button, ZoneDepot, type FichierChoisi } from '@artosera/ui';
import { lireAccueilDepot, transportDepotPublic } from '../../api/depotPublic.js';
import { AuthCard } from '../AuthCard.js';
import styles from './DepotPublicPage.module.css';

/** Un dossier peut compter des milliers de fichiers : seuls ceux en vol ou en échec sont listés. */
const LIGNES_MAX = 50;

/**
 * `/depot/:jeton` (A5, ARTO-129) : la page sans compte par laquelle une
 * galerie envoie l'export de son ancien logiciel. Fichiers ou dossiers
 * entiers, une barre par fichier, la reprise après coupure. Rien ne s'y
 * relit : l'API n'offre ici que l'envoi. Aucun script tiers.
 */
export function DepotPublicPage(): JSX.Element {
  return (
    <>
      {/* Le jeton est dans l'URL : aucune requête de la page ne le transmet en Referer. */}
      <meta name="referrer" content="no-referrer" />
      <DepotPublic />
    </>
  );
}

function DepotPublic(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const jeton = useParams<{ jeton: string }>().jeton ?? '';
  const accueil = useQuery({
    queryKey: ['depot-public', jeton],
    queryFn: ({ signal }) => lireAccueilDepot(jeton, signal),
    retry: false,
  });

  const [fichiers, setFichiers] = useState<FichierChoisi[]>([]);
  const [suivis, setSuivis] = useState<SuiviEnvoiFichier[]>([]);
  const [enCours, setEnCours] = useState(false);
  const controleur = useRef<AbortController | null>(null);
  // Les progrès arrivent par dizaines à la seconde : l'écran se redessine au plus cinq fois.
  const tampon = useRef<SuiviEnvoiFichier[]>([]);
  const minuterie = useRef<number | null>(null);

  useEffect(() => () => controleur.current?.abort(), []);

  const publier = () => {
    minuterie.current = null;
    setSuivis([...tampon.current]);
  };

  async function lancer(lot: FichierChoisi[]): Promise<void> {
    if (lot.length === 0) return;
    controleur.current = new AbortController();
    const signal = controleur.current.signal;
    setFichiers(lot);
    setEnCours(true);
    tampon.current = lot.map((choisi) => ({
      chemin: choisi.chemin,
      octets: choisi.fichier.size,
      envoyes: 0,
      etat: 'attente',
      erreur: null,
      raison: null,
    }));
    publier();
    try {
      const resultat = await envoyerFichiersDepot(
        lot.map((choisi) => ({
          chemin: choisi.chemin,
          octets: choisi.fichier.size,
          tranche: (debut: number, fin: number) => choisi.fichier.slice(debut, fin),
        })),
        await transportDepotPublic(jeton, signal),
        {
          signal,
          surSuivi: (index, suivi) => {
            tampon.current[index] = suivi;
            minuterie.current ??= window.setTimeout(publier, 200);
          },
        },
      );
      tampon.current = [...resultat];
      publier();
    } finally {
      setEnCours(false);
      void accueil.refetch();
    }
  }

  if (accueil.isLoading)
    return (
      <AuthCard title={t('depotPublic.titre')} large>
        <p className={styles.texte}>{t('depotPublic.chargement')}</p>
      </AuthCard>
    );
  if (!accueil.isSuccess)
    return (
      <AuthCard title={t('depotPublic.titre')} large>
        <p className={styles.erreur} role="alert">
          {t('depotPublic.invalide')}
        </p>
      </AuthCard>
    );

  const echecs = suivis.filter((suivi) => suivi.etat === 'erreur');
  const faits = suivis.filter((suivi) => suivi.etat === 'termine').length;
  const volume = suivis.reduce((somme, suivi) => somme + suivi.octets, 0);
  const envoyes = suivis.reduce((somme, suivi) => somme + suivi.envoyes, 0);
  const visibles = suivis
    .map((suivi, index) => ({ suivi, index }))
    .filter(({ suivi }) => suivi.etat === 'envoi' || suivi.etat === 'erreur');
  const fini = !enCours && suivis.length > 0 && echecs.length === 0;

  return (
    <AuthCard title={t('depotPublic.titre')} subtitle={t('depotPublic.sousTitre')} large>
      <div className={styles.pile}>
        <p className={styles.texte}>
          {t('depotPublic.validite', {
            date: formatDate(new Date(accueil.data.expireLe), langue, 'court'),
            restant: formatOctets(accueil.data.octetsRestants, langue),
          })}
        </p>
        <p className={styles.discret}>{t('depotPublic.confidentialite')}</p>
        <ZoneDepot
          desactive={enCours}
          onFichiers={(lot) => void lancer(lot)}
          libelles={{
            consigne: t('depotPublic.zone.consigne'),
            fichiers: t('depotPublic.zone.fichiers'),
            dossier: t('depotPublic.zone.dossier'),
          }}
        />
        <p className={styles.discret}>{t('depotPublic.reprise')}</p>

        {suivis.length > 0 ? (
          <div className={styles.pile} aria-live="polite">
            <p className={styles.texte}>
              {t('depotPublic.resume', {
                faits,
                total: suivis.length,
                envoyes: formatOctets(envoyes, langue),
                volume: formatOctets(volume, langue),
              })}
            </p>
            <progress
              className={styles.barre}
              max={Math.max(volume, 1)}
              value={envoyes}
              aria-label={t('depotPublic.enCours')}
            />
            {enCours ? <p className={styles.discret}>{t('depotPublic.enCours')}</p> : null}
            {visibles.length > 0 ? (
              <ul className={styles.envois}>
                {visibles.slice(0, LIGNES_MAX).map(({ suivi, index }) => (
                  <li key={`${String(index)}-${suivi.chemin}`} data-etat={suivi.etat}>
                    <span className={styles.chemin}>{suivi.chemin}</span>
                    <progress
                      className={styles.barre}
                      max={Math.max(suivi.octets, 1)}
                      value={suivi.envoyes}
                      aria-label={t('depotPublic.progression', { chemin: suivi.chemin })}
                    />
                    <span className={styles.discret}>
                      {suivi.etat === 'erreur'
                        ? ((suivi.raison === null ? null : suivi.erreur) ?? t('depotPublic.erreur'))
                        : `${formatOctets(suivi.envoyes, langue)} / ${formatOctets(suivi.octets, langue)}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {visibles.length > LIGNES_MAX ? (
              <p className={styles.discret}>
                {t('depotPublic.autres', { count: visibles.length - LIGNES_MAX })}
              </p>
            ) : null}
            {!enCours && echecs.length > 0 ? (
              <p className={styles.avertissement} role="status">
                {t('depotPublic.echecs', { count: echecs.length })}
              </p>
            ) : null}
            {fini ? (
              <p className={styles.succes} role="status">
                {t('depotPublic.fini')}
              </p>
            ) : null}
            <div className={styles.actions}>
              {enCours ? (
                <Button variant="ghost" onClick={() => controleur.current?.abort()}>
                  {t('depotPublic.interrompre')}
                </Button>
              ) : null}
              {!enCours && echecs.length > 0 ? (
                <Button
                  onClick={() =>
                    void lancer(fichiers.filter((_, index) => suivis[index]?.etat !== 'termine'))
                  }
                >
                  {t('depotPublic.reprendre')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </AuthCard>
  );
}
